import React, { useState } from 'react'
import { UserPlus, Phone, Mail, Building, Pencil, Check, X, Trash2, Car } from 'lucide-react'
import { toast } from 'sonner'

const API_URL = process.env.REACT_APP_BACKEND_URL

const CONTACT_TYPES = [
  { value: 'gestionnaire_compagnie', label: 'Gestionnaire compagnie', icon: Building },
  { value: 'expert', label: 'Expert', icon: UserPlus },
  { value: 'reparateur', label: 'Reparateur', icon: UserPlus },
  { value: 'tiers', label: 'Tiers', icon: UserPlus },
  { value: 'depot', label: 'Depot', icon: Building },
  { value: 'autre', label: 'Autre', icon: UserPlus },
]

const EMPTY_CONTACT = { type: 'gestionnaire_compagnie', nom: '', societe: '', telephone: '', email: '', plaque: '', marque: '', modele: '', compagnie_adverse: '' }

export default function ContactsDossier({ sinistreId, contacts = [], onRefresh }) {
  const [editing, setEditing] = useState(null) // index or 'new'
  const [form, setForm] = useState({ ...EMPTY_CONTACT })
  const [saving, setSaving] = useState(false)

  async function saveContacts(updated) {
    setSaving(true)
    try {
      const res = await fetch(`${API_URL}/api/sinistres/${sinistreId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ contacts_dossier: updated }),
      })
      const data = await res.json()
      if (data.success) {
        toast.success('Contacts mis à jour')
        setEditing(null)
        setForm({ ...EMPTY_CONTACT })
        onRefresh()
      } else {
        toast.error(data.detail || 'Erreur sauvegarde')
      }
    } catch {
      toast.error('Erreur réseau')
    } finally {
      setSaving(false)
    }
  }

  function handleAdd() {
    setForm({ ...EMPTY_CONTACT })
    setEditing('new')
  }

  function handleEdit(idx) {
    setForm({ ...contacts[idx] })
    setEditing(idx)
  }

  function handleSave() {
    if (!form.nom.trim()) { toast.error('Le nom est requis'); return }
    let updated
    if (editing === 'new') {
      updated = [...contacts, { ...form }]
    } else {
      updated = contacts.map((c, i) => i === editing ? { ...form } : c)
    }
    saveContacts(updated)
  }

  function handleDelete(idx) {
    saveContacts(contacts.filter((_, i) => i !== idx))
  }

  function handleCancel() {
    setEditing(null)
    setForm({ ...EMPTY_CONTACT })
  }

  const typeLabel = (t) => CONTACT_TYPES.find(ct => ct.value === t)?.label || t

  return (
    <div data-testid="contacts-dossier">
      {/* Existing contacts */}
      <div className="space-y-3">
        {contacts.length === 0 && editing === null && (
          <p className="text-xs text-slate-400 text-center py-2">Aucun contact renseigné</p>
        )}
        {contacts.map((c, idx) => (
          <div key={idx} className="group flex items-start gap-3 p-2 rounded-lg hover:bg-slate-50 transition-colors" data-testid={`contact-row-${idx}`}>
            <div className="w-8 h-8 rounded-full bg-blue-50 flex items-center justify-center text-xs font-bold text-blue-600 flex-shrink-0">
              {c.nom?.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[10px] text-blue-500 font-medium uppercase tracking-wider">{typeLabel(c.type)}</p>
              <p className="text-sm font-medium text-slate-800 truncate">{c.nom}</p>
              {c.societe && <p className="text-xs text-slate-500 truncate">{c.societe}</p>}
              <div className="flex flex-wrap gap-x-3 mt-0.5">
                {c.telephone && (
                  <a href={`tel:${c.telephone}`} className="text-xs text-slate-500 hover:text-blue-600 flex items-center gap-1">
                    <Phone size={10} />{c.telephone}
                  </a>
                )}
                {c.email && (
                  <a href={`mailto:${c.email}`} className="text-xs text-slate-500 hover:text-blue-600 flex items-center gap-1">
                    <Mail size={10} />{c.email}
                  </a>
                )}
              </div>
              {c.type === 'tiers' && (c.plaque || c.marque || c.modele || c.compagnie_adverse) && (
                <div className="flex flex-wrap gap-x-3 mt-1 text-[10px] text-slate-500">
                  {c.plaque && <span className="flex items-center gap-0.5"><Car size={9} />{c.plaque}</span>}
                  {(c.marque || c.modele) && <span>{[c.marque, c.modele].filter(Boolean).join(' ')}</span>}
                  {c.compagnie_adverse && <span className="flex items-center gap-0.5"><Building size={9} />{c.compagnie_adverse}</span>}
                </div>
              )}
            </div>
            <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0">
              <button onClick={() => handleEdit(idx)} className="p-1 rounded hover:bg-slate-200" title="Modifier" data-testid={`edit-contact-${idx}`}>
                <Pencil size={12} className="text-slate-400" />
              </button>
              <button onClick={() => handleDelete(idx)} className="p-1 rounded hover:bg-red-50" title="Supprimer" data-testid={`delete-contact-${idx}`}>
                <Trash2 size={12} className="text-slate-300 hover:text-red-500" />
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Edit/Add form */}
      {editing !== null && (
        <div className="mt-3 p-3 bg-blue-50/50 border border-blue-100 rounded-lg space-y-2" data-testid="contact-form">
          <div className="flex flex-wrap gap-1.5">
            {CONTACT_TYPES.map(ct => (
              <button
                key={ct.value}
                type="button"
                onClick={() => setForm(prev => ({ ...prev, type: ct.value }))}
                className={`text-[10px] px-2 py-0.5 rounded-full border font-medium transition-colors ${
                  form.type === ct.value
                    ? 'bg-blue-100 text-blue-700 border-blue-200'
                    : 'bg-white text-slate-400 border-slate-200 hover:border-slate-300'
                }`}
              >
                {ct.label}
              </button>
            ))}
          </div>
          <input
            value={form.nom}
            onChange={e => setForm(prev => ({ ...prev, nom: e.target.value }))}
            placeholder="Nom *"
            className="w-full px-2.5 py-1.5 text-xs border border-slate-200 rounded-lg bg-white outline-none focus:ring-1 focus:ring-blue-400"
            data-testid="contact-nom-input"
          />
          <input
            value={form.societe}
            onChange={e => setForm(prev => ({ ...prev, societe: e.target.value }))}
            placeholder="Société / Cabinet"
            className="w-full px-2.5 py-1.5 text-xs border border-slate-200 rounded-lg bg-white outline-none focus:ring-1 focus:ring-blue-400"
            data-testid="contact-societe-input"
          />
          <div className="grid grid-cols-2 gap-2">
            <input
              value={form.telephone}
              onChange={e => setForm(prev => ({ ...prev, telephone: e.target.value }))}
              placeholder="Téléphone"
              className="px-2.5 py-1.5 text-xs border border-slate-200 rounded-lg bg-white outline-none focus:ring-1 focus:ring-blue-400"
              data-testid="contact-tel-input"
            />
            <input
              value={form.email}
              onChange={e => setForm(prev => ({ ...prev, email: e.target.value }))}
              placeholder="Email"
              className="px-2.5 py-1.5 text-xs border border-slate-200 rounded-lg bg-white outline-none focus:ring-1 focus:ring-blue-400"
              data-testid="contact-email-input"
            />
          </div>
          {/* Champs tiers: vehicule + compagnie adverse */}
          {form.type === 'tiers' && (
            <div className="space-y-2 pt-1 border-t border-blue-100">
              <p className="text-[10px] font-medium text-blue-600 uppercase tracking-wider">Vehicule tiers</p>
              <div className="grid grid-cols-3 gap-2">
                <input
                  value={form.plaque || ''}
                  onChange={e => setForm(prev => ({ ...prev, plaque: e.target.value }))}
                  placeholder="Plaque"
                  className="px-2.5 py-1.5 text-xs border border-slate-200 rounded-lg bg-white outline-none focus:ring-1 focus:ring-blue-400"
                  data-testid="contact-plaque-input"
                />
                <input
                  value={form.marque || ''}
                  onChange={e => setForm(prev => ({ ...prev, marque: e.target.value }))}
                  placeholder="Marque"
                  className="px-2.5 py-1.5 text-xs border border-slate-200 rounded-lg bg-white outline-none focus:ring-1 focus:ring-blue-400"
                  data-testid="contact-marque-input"
                />
                <input
                  value={form.modele || ''}
                  onChange={e => setForm(prev => ({ ...prev, modele: e.target.value }))}
                  placeholder="Modele"
                  className="px-2.5 py-1.5 text-xs border border-slate-200 rounded-lg bg-white outline-none focus:ring-1 focus:ring-blue-400"
                  data-testid="contact-modele-input"
                />
              </div>
              <input
                value={form.compagnie_adverse || ''}
                onChange={e => setForm(prev => ({ ...prev, compagnie_adverse: e.target.value }))}
                placeholder="Compagnie d'assurance adverse"
                className="w-full px-2.5 py-1.5 text-xs border border-slate-200 rounded-lg bg-white outline-none focus:ring-1 focus:ring-blue-400"
                data-testid="contact-compagnie-adverse-input"
              />
            </div>
          )}
          <div className="flex justify-end gap-1.5 pt-1">
            <button onClick={handleCancel} className="p-1.5 rounded-lg hover:bg-slate-100" title="Annuler" disabled={saving}>
              <X size={14} className="text-slate-400" />
            </button>
            <button
              onClick={handleSave}
              disabled={saving || !form.nom.trim()}
              className="flex items-center gap-1 px-3 py-1.5 bg-blue-500 text-white text-xs font-medium rounded-lg hover:bg-blue-600 disabled:opacity-50 transition-colors"
              data-testid="contact-save-btn"
            >
              <Check size={12} />
              {saving ? 'Enregistrement...' : 'Enregistrer'}
            </button>
          </div>
        </div>
      )}

      {/* Add button */}
      {editing === null && (
        <button
          onClick={handleAdd}
          className="mt-3 w-full flex items-center justify-center gap-1.5 py-2 text-xs text-blue-500 font-medium border border-dashed border-blue-200 rounded-lg hover:bg-blue-50 transition-colors"
          data-testid="add-contact-btn"
        >
          <UserPlus size={12} />
          Ajouter un contact
        </button>
      )}
    </div>
  )
}
